import { ImgID } from "../../image_loader/img_id.js";
import { ImagesHolder } from "../../image_loader/images_holder.js";
import { UIImage } from "./ui_image.js";
import { UIElementHolder } from "./ui_element_holder.js";
import { ColorScheme } from "./color_scheme.js";
import { AvatarUtils } from "../../common/avatar_utils.js";
import { loadFacebookAvatar } from "../../common/facebook_avatar_loader.js";
import { RoundedRectangleFill } from "./rounded_rectangle.js";
import { TelegramAvatar, TelegramAvatarIdentity } from "../../common/telegram_avatar.js";

export class UIAvatarSelectionItem extends UIElementHolder
{
  public avatar_id: number;
  private is_selected: boolean = false;
  private on_click: () => void;
  private avatar_image!: UIImage;
  private border_size_offset: number;
  private frame_size_offset: number;
  private registration_icon_id?: ImgID;
  private border_color: RoundedRectangleFill;
  private facebook_av_url?: string | null;
  private telegram_identity: TelegramAvatarIdentity;
  // Photo url whose bitmap is currently in avatar_image; until then the
  // Telegram avatar is drawn as initials.
  private telegram_photo_shown: string | null = null;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    avatar_id: number,
    on_click: () => void,
    border_size_offset: number,
    frame_size_offset: number,
    registration_icon_id?: ImgID,
    is_scalable_on_click = false,
    border_color?: RoundedRectangleFill,
    facebook_av_url?: string | null,
    telegram_identity?: TelegramAvatarIdentity
  )
  {
    super( ctx, x, y, width, height, false, is_scalable_on_click );
    this.telegram_identity = telegram_identity ?? TelegramAvatar.currentIdentity();
    this.avatar_id = avatar_id;
    this.on_click = on_click;
    this.border_size_offset = border_size_offset;
    this.frame_size_offset = frame_size_offset;
    this.registration_icon_id = registration_icon_id;
    this.border_color = border_color ?? ColorScheme.avatar_stroke;
    this.facebook_av_url = facebook_av_url;

    this.createAvatarImage();
    this.setOnClick( () =>
    {
      this.on_click();
    } );
  }

  public setAvatarId( avatar_id: number, facebook_av_url?: string | null )
  {
    this.avatar_id = avatar_id;
    if ( facebook_av_url !== undefined )
      this.facebook_av_url = facebook_av_url;

    this.avatar_image.setImageId( AvatarUtils.getAvatarImgId( avatar_id ) );
    this.layoutAvatarImage();
    this.telegram_photo_shown = null;
    this.applyFacebookPhotoIfNeeded();
    this.applyTelegramPhotoIfNeeded();
    this.forceRedraw();
  }

  private borderSize(): number
  {
    return AvatarUtils.hasBakedFrame( this.avatar_id ) ? 0 : this.border_size_offset;
  }

  private layoutAvatarImage()
  {
    const border = this.borderSize();
    const avatar_size = this.rect.getW() - border * 2;

    const image_rect = this.avatar_image.getRect();
    image_rect.setX( border );
    image_rect.setY( border );
    image_rect.resize( avatar_size, avatar_size );
  }

  private createAvatarImage()
  {
    const border = this.borderSize();
    const avatar_size = this.rect.getW() - border * 2;
    const avatar_x = border;
    const avatar_y = border;

    const avatar_img_id = AvatarUtils.getAvatarImgId( this.avatar_id );

    this.avatar_image = new UIImage(
      this.ctx,
      avatar_x,
      avatar_y,
      avatar_size,
      avatar_size,
      avatar_img_id
    );

    this.addChild( this.avatar_image, true );

    this.applyFacebookPhotoIfNeeded();
    this.applyTelegramPhotoIfNeeded();
  }

  // The Telegram avatar shows initials until the photo (if any) has loaded.
  private applyTelegramPhotoIfNeeded()
  {
    const url = this.telegram_identity.photo_url;
    if ( !AvatarUtils.isTelegramAvatar( this.avatar_id ) || !url )
      return;

    TelegramAvatar.loadPhoto( url )
      .then( bitmap =>
      {
        if ( !AvatarUtils.isTelegramAvatar( this.avatar_id ) || this.telegram_identity.photo_url !== url )
          return;

        this.avatar_image.setImageBitmap( bitmap );
        this.telegram_photo_shown = url;
        this.forceRenderParent();
      } )
      .catch( () => { /* keep the initials */ } );
  }

  // For the Facebook avatar (id 987) the placeholder ImgID is already set; if we
  // have the real photo URL, fetch it and swap in the decoded bitmap.
  private applyFacebookPhotoIfNeeded()
  {
    if ( this.avatar_id !== AvatarUtils.FACEBOOK_AVATAR_ID || !this.facebook_av_url )
      return;

    const url = this.facebook_av_url;
    loadFacebookAvatar( url )
      .then( bitmap =>
      {
        // The avatar/url may have changed while the photo was loading.
        if ( this.avatar_id === AvatarUtils.FACEBOOK_AVATAR_ID && this.facebook_av_url === url )
        {
          this.avatar_image.setImageBitmap( bitmap );
          // Repaint the parent (scroll row / card) so the photo replaces the
          // placeholder even though scroll rows are rendered only once.
          this.forceRenderParent();
        }
      } )
      .catch( () => { /* keep placeholder on failure */ } );
  }

  public setSelected( selected: boolean )
  {
    this.is_selected = selected;
    this.setShouldBeRendered();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();

    if ( !AvatarUtils.hasBakedFrame( this.avatar_id ) )
      this.drawBorder();

    const avatar_size = this.rect.getW() - this.borderSize() * 2;
    const radius = avatar_size / 2;

    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.arc( 0, 0, radius, 0, Math.PI * 2 );
    this.ctx.clip();

    // Children always render here: finishRender() would otherwise draw them
    // again outside the clip. The initials then cover the placeholder image.
    this.ctx.save();
    this.renderChildren();
    this.ctx.restore();

    if ( AvatarUtils.isTelegramAvatar( this.avatar_id ) && this.telegram_photo_shown === null )
      TelegramAvatar.drawInitials( this.ctx, this.telegram_identity, radius );

    this.ctx.restore();

    if ( this.registration_icon_id )
      this.drawRegistrationIcon();

    if ( this.is_selected )
      this.drawSelectedImage();

    this.finishRender();
  }

  private drawBorder()
  {
    const radius = this.rect.getW() / 2 + this.border_size_offset / 2;

    this.ctx.beginPath();
    this.ctx.arc( 0, 0, radius, 0, Math.PI * 2 );
    this.ctx.fillStyle = this.createBorderFillStyle( radius );
    this.ctx.fill();
  }

  private createBorderFillStyle( radius: number ): string | CanvasGradient
  {
    if ( typeof this.border_color === "string" )
      return this.border_color;

    const gradient = this.border_color.direction === "horizontal"
      ? this.ctx.createLinearGradient( -radius, 0, radius, 0 )
      : this.ctx.createLinearGradient( 0, -radius, 0, radius );

    if ( this.border_color.stops )
    {
      for ( const stop of this.border_color.stops )
        gradient.addColorStop( stop.offset, stop.color );
    }
    else
    {
      gradient.addColorStop( 0, this.border_color.from ?? "#000000" );
      gradient.addColorStop( 1, this.border_color.to ?? "#000000" );
    }

    return gradient;
  }

  private drawRegistrationIcon()
  {
    const design_icon_size = 50;
    const icon_size = LayoutScaler.widthFromDesign( design_icon_size );
    const x = this.rect.getW2() - icon_size;
    const y = this.rect.getH2() - icon_size;

    this.ctx.drawImage(
      ImagesHolder.getImage( this.registration_icon_id! ),
      x,
      y,
      icon_size,
      icon_size
    );
  }

  private drawSelectedImage()
  {
    const design_size = 90;
    const design_offset_x = 18;
    const design_offset_y = 4;
    const size = LayoutScaler.widthFromDesign( design_size );
    const offset_x = LayoutScaler.widthFromDesign( design_offset_x );
    const offset_y = LayoutScaler.heightFromDesign( design_offset_y );
    const x = this.rect.getW2() - size + offset_x;
    const y = this.rect.getH2() - size + offset_y;

    this.ctx.drawImage(
      ImagesHolder.getImage( ImgID.IconAvatarSelected ),
      x,
      y,
      size,
      size,
    );
  }

  public name(): string
  {
    return "UIAvatarSelectionItem";
  }
}
